export interface GuidedPrompt {
  text: string;
  questionType: string;
  options?: Array<{ label: string }>;
  required?: boolean;
}

export interface GuidedPromptAnswer {
  selectedOptions: string[];
  freeformText: string;
}

export type GuidedPromptAnswers = Record<
  number,
  GuidedPromptAnswer | undefined
>;

/**
 * Formats a set of guided-prompt answers into a plain-text Q&A string.
 * Skipped questions are emitted as `(skipped)`.
 *
 * @param {GuidedPrompt[]} prompts - Guided prompt bodies.
 * @param {GuidedPromptAnswers} answers - Guided prompt answers.
 * @returns {string | null} Formatted Q&A text. `null` when no answers were provided.          s
 */
export const formatGuidedPromptAnswers = (
  prompts: GuidedPrompt[],
  answers: GuidedPromptAnswers
): string | null => {
  const hasAnyAnswer = Object.values(answers).some(
    (answer) =>
      (answer?.selectedOptions?.length ?? 0) > 0 ||
      Boolean(answer?.freeformText?.trim())
  );

  if (!hasAnyAnswer) return null;

  return prompts
    .map((prompt, index) => {
      const answer = answers[index] ?? {
        selectedOptions: [],
        freeformText: '',
      };
      const answerParts = [...answer.selectedOptions];

      if (answer.freeformText?.trim()) {
        answerParts.push(answer.freeformText.trim());
      }

      const answerText =
        answerParts.length > 0 ? answerParts.join(', ') : '(skipped)';

      return `Q: ${prompt.text}\nA: ${answerText}`;
    })
    .join('\n\n');
};
